import "@/i18n";

import { useEffect, Suspense, type ReactNode } from "react";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import { ConciergeResumeHandler } from "@/components/ConciergeResumeHandler";
import RouteSEO from "@/components/seo/RouteSEO";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { scheduleMonitoringBoot } from "@/integrations/monitoring/lazyInit";
import { initTikTokPixel } from "@/integrations/monitoring/tiktok";
import { captureAttribution } from "@/lib/attribution";

import appCss from "../styles.css?url";

const ORG_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Amorette",
  url: "https://getamorette.com/",
  logo: "https://getamorette.com/favicon.png",
  email: "hello@getamorette.com",
  sameAs: [],
});

const WEBSITE_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Amorette",
  url: "https://getamorette.com/",
  description:
    "Amorette connects singles through elegant, curated dinner experiences. Discover meaningful connections over exceptional dining.",
  publisher: { "@type": "Organization", name: "Amorette", url: "https://getamorette.com/" },
});

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { title: "Amorette - Elegant Connections, Curated Dinners" },
      {
        name: "description",
        content:
          "Amorette connects singles through elegant, curated dinner experiences. Discover meaningful connections over exceptional dining.",
      },
      { name: "author", content: "Amorette" },
      { property: "og:title", content: "Amorette - Elegant Connections, Curated Dinners" },
      {
        property: "og:description",
        content:
          "Amorette connects singles through elegant, curated dinner experiences. Discover meaningful connections over exceptional dining.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://getamorette.com/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@AmoretteApp" },
      { name: "twitter:image", content: "https://getamorette.com/og-image.png" },
      { name: "theme-color", content: "#4a1942" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", href: "/favicon-48x48.png", sizes: "48x48" },
      { rel: "icon", type: "image/png", href: "/favicon.png", sizes: "96x96" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Alex+Brush&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap",
      },
    ],
    scripts: [
      { children: STALE_CHUNK_BOOTSTRAP },
      { type: "application/ld+json", children: ORG_JSON_LD },
      { type: "application/ld+json", children: WEBSITE_JSON_LD },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootShell({ children }: { children: ReactNode }) {
  // class="dark" is rendered into the HTML itself so the dark brand theme
  // applies even before (or without) any JavaScript running.
  return (
    <html lang="en" className="dark" style={{ colorScheme: "dark" }} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

// Global loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Runs from the page head, before the app's module graph is fetched, so a
// deleted entry file cannot leave the reader on a blank tab: the same bounded
// fresh-fetch recovery used below, plus a readable fallback panel if every
// attempt fails. Shares its budget keys with the React-side recovery so the
// two can never stack up into a reload loop.
const STALE_CHUNK_BOOTSTRAP = `
(function () {
  var A = "amorette_chunk_recover_attempts";
  var M = "amorette_chunk_recover_at";
  var WINDOW = 20000;
  var MAX = 2;
  function read(key) {
    try { return Number(sessionStorage.getItem(key) || "0"); } catch (e) { return 0; }
  }
  function looksLikeMissingFile(message) {
    return /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS|Loading chunk [\\d]+ failed|Loading CSS chunk [\\d]+ failed/i.test(message || "");
  }
  function isMissingAppFile(target) {
    if (!target || !target.tagName) return false;
    var tag = target.tagName.toUpperCase();
    if (tag !== "SCRIPT" && tag !== "LINK") return false;
    var ref = target.src || target.href || "";
    return !!ref && ref.indexOf(location.origin) === 0;
  }
  function freshUrl() {
    var here = location.href, hash = here.indexOf("#");
    var base = hash === -1 ? here : here.slice(0, hash);
    var tail = hash === -1 ? "" : here.slice(hash);
    return base + (base.indexOf("?") === -1 ? "?" : "&") + "_r=" + Date.now() + tail;
  }
  function showFallback() {
    if (!document.body || /This page didn/.test(document.body.innerText || "")) return;
    var panel = document.createElement("div");
    panel.setAttribute("role", "alert");
    panel.style.cssText = "position:fixed;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;background:#0d0a12;color:#f5f0e8;font:16px/1.5 -apple-system,system-ui,sans-serif;text-align:center;z-index:9999";
    panel.innerHTML = '<div style="max-width:26rem"><p style="font-size:19px;margin:0 0 8px">This page didn&#39;t load</p>' +
      '<p style="color:#b9ae9d;margin:0 0 18px">Something went wrong on our end. You can try again or head back home.</p>' +
      '<a id="amorette-retry" href="#" style="color:#d4af37;font-weight:600;text-decoration:underline;margin-right:18px">Try again</a>' +
      '<a href="/" style="color:#d4af37;font-weight:600;text-decoration:underline">Go home</a></div>';
    document.body.appendChild(panel);
    var retry = document.getElementById("amorette-retry");
    if (retry) retry.addEventListener("click", function (ev) {
      ev.preventDefault();
      try { sessionStorage.removeItem(A); sessionStorage.removeItem(M); } catch (e) {}
      location.replace(freshUrl());
    });
  }
  function recover(fileUrl) {
    // A browser that remembered this failed response would hand the fresh page
    // the same failure, so ask for the file once more from the network first.
    try {
      if (fileUrl && typeof fetch === "function") {
        fetch(fileUrl, { cache: "reload", mode: "no-cors" }).catch(function () {});
      }
    } catch (e) {}
    var now = Date.now(), attempts = read(A);
    if (now - read(M) > WINDOW) attempts = 0;
    if (attempts === MAX || attempts > MAX) { showFallback(); return; }
    try {
      sessionStorage.setItem(A, String(attempts + 1));
      sessionStorage.setItem(M, String(now));
    } catch (e) {}
    try { location.replace(freshUrl()); } catch (e) { location.reload(); }
  }
  addEventListener("error", function (event) {
    var target = event && event.target;
    if (isMissingAppFile(target)) { recover(target.src || target.href); return; }
    if (looksLikeMissingFile(event && event.message)) recover(null);
  }, true);
  addEventListener("unhandledrejection", function (event) {
    var reason = event && event.reason;
    if (looksLikeMissingFile((reason && reason.message) || String(reason || ""))) recover();
  }, true);
  // Last resort: if nothing ever painted, the reader still gets a way out
  // instead of a blank tab. Only fires when the page has no text at all.
  setTimeout(function () {
    try {
      if (!(document.body && (document.body.innerText || "").trim().length === 0)) return;
    } catch (e) { return; }
    showFallback();
  }, 10000);
})();
`;

// Deploys replace hashed asset files, so a tab left open across a deploy keeps
// asking for chunks that no longer exist. The app then crashes at boot or on
// the next lazy import — Safari/WebKit reports "Importing a module script
// failed.", Chrome reports "Failed to fetch dynamically imported module".
// Recover with a small, bounded number of fresh document fetches. A plain
// reload is not enough: the browser can re-resolve the same module graph it
// already failed, and Safari can reuse a remembered copy of the page. A
// one-off query marker forces a document response that has never been cached.
const isChunkLoadError = (msg: unknown): boolean => {
  const s = typeof msg === "string" ? msg : (msg as { message?: string })?.message || "";
  return /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS|Loading chunk [\d]+ failed|Loading CSS chunk [\d]+ failed/i.test(s);
};

const ATTEMPTS_KEY = "amorette_chunk_recover_attempts";
const MARKED_AT_KEY = "amorette_chunk_recover_at";
const RECOVER_PARAM = "_r";
const RECOVER_WINDOW_MS = 20_000;
const MAX_RECOVER_ATTEMPTS = 2;

const readCount = (key: string): number => {
  try {
    return Number(sessionStorage.getItem(key) || "0");
  } catch {
    return 0;
  }
};

// Full-document navigation to the same page with a unique marker, so both the
// page and the module graph behind it are fetched again from the network.
const navigateFresh = (): void => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RECOVER_PARAM, String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
};

// Automatic recovery: bounded so a genuinely broken page cannot loop.
const recoverFromStaleChunk = (): void => {
  const now = Date.now();
  let attempts = readCount(ATTEMPTS_KEY);
  if (now - readCount(MARKED_AT_KEY) > RECOVER_WINDOW_MS) attempts = 0;
  if (attempts >= MAX_RECOVER_ATTEMPTS) return; // leave the readable error screen up
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, String(attempts + 1));
    sessionStorage.setItem(MARKED_AT_KEY, String(now));
  } catch {
    /* storage unavailable — one fresh fetch is still worth attempting */
  }
  navigateFresh();
};

// Recovery requested by the reader through the error screen: always allowed,
// so nobody is stranded behind an exhausted automatic budget.
const forceRecoverFromStaleChunk = (): void => {
  try {
    sessionStorage.removeItem(ATTEMPTS_KEY);
    sessionStorage.removeItem(MARKED_AT_KEY);
  } catch {
    /* ignore */
  }
  navigateFresh();
};

// The marker exists only to defeat caches, so remove it from the address bar
// as soon as the fresh page boots — it must never reach a shared link or an
// analytics page path.
const clearRecoverMarker = (): void => {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(RECOVER_PARAM)) {
      url.searchParams.delete(RECOVER_PARAM);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch {
    /* ignore */
  }
};

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // ported from main.tsx — browser-only boot sequence, runs after hydration
  useEffect(() => {
    // Drop the cache-busting marker left by stale-chunk recovery before any
    // analytics or page tracking reads the address.
    clearRecoverMarker();

    // Capture UTM params on first load (persisted to session + 30d cookie)
    captureAttribution();

    // TikTok pixel loads ~640 KB of vendor JS — schedule just after first paint.
    const bootTikTok = () => initTikTokPixel();
    if (typeof (window as unknown as { requestIdleCallback?: unknown }).requestIdleCallback === "function") {
      (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback(bootTikTok, { timeout: 2000 });
    } else {
      setTimeout(bootTikTok, 1200);
    }

    // Sentry + Mixpanel + GA are deferred until first interaction or idle fallback.
    scheduleMonitoringBoot();

    // Warm the sign-in/sign-up page chunk on idle so CTA taps open it
    // instantly instead of freezing while its code downloads and parses.
    const warmAuth = () => {
      void import("@/pages/Auth");
    };
    if (typeof (window as unknown as { requestIdleCallback?: unknown }).requestIdleCallback === "function") {
      (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback(warmAuth, { timeout: 4000 });
    } else {
      setTimeout(warmAuth, 2500);
    }

    // Auto-recover from stale lazy-chunk imports after a new deploy.
    const onError = (e: ErrorEvent) => {
      if (isChunkLoadError(e.message) || isChunkLoadError(e.error)) recoverFromStaleChunk();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      if (isChunkLoadError(e.reason)) recoverFromStaleChunk();
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // Clear stale service workers so iPhone Safari cannot serve an old cached auth bundle
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => {
          registrations.forEach((registration) => registration.unregister());
        })
        .catch(() => {
          // no-op
        });
    }

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <ScrollToTop />
            <RouteSEO />
            <ConciergeResumeHandler />
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

function RootError({ error }: { error: unknown; reset: () => void }) {
  // Short, on-device diagnostic so failures can be identified from a phone
  // screenshot without desktop tools. Message text only, truncated.
  const errorDetail = (() => {
    const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
    return raw ? `Error: ${raw.slice(0, 160)}` : "";
  })();
  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    // React.lazy chunk failures are caught here without ever reaching the
    // window error listeners above — fetch a fresh page and module graph.
    if (isChunkLoadError(error)) recoverFromStaleChunk();
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-semibold">This page didn&apos;t load</h1>
        <p className="text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        {errorDetail ? (
          <p className="text-xs text-muted-foreground/70 break-words font-mono" data-testid="root-error-detail">
            {errorDetail}
          </p>
        ) : null}
        <div className="flex gap-2 justify-center flex-wrap">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              // Always allowed: fetches the current page and assets fresh,
              // even after the automatic retries above are used up.
              forceRecoverFromStaleChunk();
            }}
          >
            Try again
          </button>
          <a href="/" className="px-4 py-2 rounded-md border border-border">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
